/**
 * components/composer/modelFallback.ts —— 会话级模型·默认兜底（★F-38 / L-PF-DFT-1，采甲）。
 *
 * 定案（session-权威件 v1.2 §8.4 第 5 条，20261022 大王逐字「U-S5甲」）：
 *   `default_model` 未设时走全局默认兜底、**发送键可用**（乙案「未设模型则恒禁用」不采用）。
 *
 * 决议序（账行 F-38 detail.code 逐字「`model == null` 时取全局默认（读配置），仍为空才拒」）：
 *   ① 会话级 `model` 非空 ⇒ 用之（source="session"）；
 *   ② `model == null` ⇒ 取**全局默认模型**（取数位在调用方，来源＝偏好/声明/模型配置，**禁硬编码模型名**）；
 *      · 取数失败（配置缺件/读取异常）⇒ **显式拒**（E_MODEL_FALLBACK_UNREADABLE，禁静默放行）；
 *      · 值非法（可用清单已装载且不含该 id）⇒ **显式拒**（E_MODEL_FALLBACK_INVALID，禁编造默认）；
 *      · 合法 ⇒ 用之（source="global_default"，发送键可用）；
 *   ③ 全局默认仍为空 ⇒ 拒（E_MODEL_FALLBACK_EMPTY）。
 *
 * 纪律：纯函数（零 DOM／零网络／零时钟）；错误码闭集＋可读中文文案（C-错误：前端不许出裸 token）。
 */

/** 兜底决议错误码（闭集；负例判据「出显式错误码」） */
export const MODEL_FALLBACK_CODES = Object.freeze({
  /** 会话级与全局默认皆空（"仍为空才拒"） */
  EMPTY: "E_MODEL_FALLBACK_EMPTY",
  /** 全局默认取数失败（配置缺件/读取异常） */
  UNREADABLE: "E_MODEL_FALLBACK_UNREADABLE",
  /** 全局默认值非法（不在可用清单） */
  INVALID: "E_MODEL_FALLBACK_INVALID",
} as const);

export type EffectiveModel =
  | { ok: true; model: string; source: "session" | "global_default" }
  | { ok: false; code: string; reason: string };

export interface EffectiveModelInput {
  /** 会话级/当前选中模型（`default_model` 首级；null/空串＝未设） */
  model?: string | null;
  /** 全局默认模型（调用方从配置读取位取数；null/空串＝未取到） */
  globalDefault?: string | null;
  /** 全局默认取数失败原因（非空即判读取失败；来源＝调用方 try/catch，不吞） */
  globalDefaultError?: string | null;
  /** 可用清单是否已装载（未装载 ≠ 无可用；未知不猜） */
  catalogLoaded?: boolean;
  /** 可用清单模型 id 集（装载后校验兜底值合法性用） */
  catalogIds?: readonly string[];
}

function trimOf(v: string | null | undefined): string {
  return typeof v === "string" ? v.trim() : "";
}

/** 会话级模型·默认兜底决议（唯一判定式；`Composer.canSubmit` 与发送路径共用） */
export function resolveEffectiveModel(input: EffectiveModelInput): EffectiveModel {
  const session = trimOf(input.model);
  if (session) return { ok: true, model: session, source: "session" };

  // ② 全局默认取数位（读取失败显式拒——禁静默放行、禁编造默认）
  const readError = trimOf(input.globalDefaultError);
  if (readError) {
    return {
      ok: false,
      code: MODEL_FALLBACK_CODES.UNREADABLE,
      reason: `默认模型配置读取失败（${readError}）：未取到可用的全局默认模型，已拒提交`,
    };
  }
  const fallback = trimOf(input.globalDefault);
  if (!fallback) {
    return {
      ok: false,
      code: MODEL_FALLBACK_CODES.EMPTY,
      reason: "未设模型且全局默认为空：请先在 设置 ›模型 里选择默认模型",
    };
  }
  // 兜底值非法 ⇒ 显式拒（可用清单已装载时校验；未装载＝未知不猜、不预判性拒）
  if (input.catalogLoaded === true) {
    const ids = Array.isArray(input.catalogIds) ? input.catalogIds : [];
    const hit = ids.some((id) => trimOf(id) === fallback);
    if (!hit) {
      return {
        ok: false,
        code: MODEL_FALLBACK_CODES.INVALID,
        reason: `全局默认模型「${fallback}」不在可用清单：请在 设置 › 模型 里重选默认模型`,
      };
    }
  }
  return { ok: true, model: fallback, source: "global_default" };
}

/** 决议失败时的守卫文案（带显式码，`[码] 文案` 形，承 F-05 带码提示形） */
export function modelGuardNote(result: Extract<EffectiveModel, { ok: false }>): string {
  return `[${result.code}] ${result.reason}`;
}
